import React, {ReactElement} from 'react';
import styled, {css} from 'styled-components';

import {RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {getCellBackgroundColor, getCellIndexStyles, getCellTextColor} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

interface TextCellProps extends TableCellProps<string> {
  renderValue?: RendererOf<string>;
  showTooltip?: boolean;
}

/*
 * Styles.
 */

const commonTextCellStyles = css<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

const StyledTextCellContainerDiv = styled.div`
  ${commonTextCellStyles}
`;

const StyledTextCellContainerTooltipCoordinator = styled(TooltipCoordinator)<TableCellStyleProps>`
  ${commonTextCellStyles}
`;

interface StyledTextCellValueDivProps {
  $isSelected: boolean;
}

const textCellValueStyles = css<StyledTextCellValueDivProps>`
  line-height: 16px;
  ${ellipsis()};

  ${(p) => getCellTextColor(p.$isSelected)}

  // If we are linkifying anything, we need to overwrite the margin/overflow/color values
  p {
    ${ellipsis()};
    margin: 0;

    a {
      ${(p) => getCellTextColor(p.$isSelected, p.theme.palette.blue.shade40)}
    }
  }
`;
export const StyledTextCellValueDiv = styled.div<StyledTextCellValueDivProps>`
  ${textCellValueStyles}
`;
const StyledTextCellValueTooltipCondition = styled(TooltipOverflowCondition)<StyledTextCellValueDivProps>``;

/*
 * Component.
 */

export const TextCell = (props: TextCellProps): ReactElement => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    renderValue,
    showTooltip,
  } = props;

  return (
    <TableCell<string>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(value) => {
        if (showTooltip) {
          return (
            <StyledTextCellContainerTooltipCoordinator
              $settings={settings}
              $isHovering={Boolean(isHovering)}
              $index={index}
              $isSelected={Boolean(isSelected)}
              $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
              $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
              render={() => (renderValue ? renderValue(value) : value)}
            >
              <StyledTextCellValueTooltipCondition $isSelected={Boolean(isSelected)}>
                {renderValue ? renderValue(value) : value}
              </StyledTextCellValueTooltipCondition>
            </StyledTextCellContainerTooltipCoordinator>
          );
        }

        return (
          <StyledTextCellContainerDiv
            $settings={settings}
            $isHovering={Boolean(isHovering)}
            $index={index}
            $isSelected={Boolean(isSelected)}
            $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
            $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          >
            <StyledTextCellValueDiv $isSelected={Boolean(isSelected)}>
              {renderValue ? renderValue(value) : value}
            </StyledTextCellValueDiv>
          </StyledTextCellContainerDiv>
        );
      }}
    />
  );
};
